import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
  ChevronDown,
  ChevronRight,
  Loader2,
  MapPin,
  Search,
  RefreshCw,
  CheckSquare,
  Square,
  Building2,
  X,
  Navigation,
  Zap,
  Package,
} from 'lucide-react';
import { MapContainer, TileLayer, Marker, Popup, useMap, CircleMarker } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAppSelector, useAppDispatch } from '../../redux/hooks';
import {
  useListPortfolioSitesQuery,
  useListAdminSitesQuery,
  useLazyGetSiteMetadataQuery,
  type PortfolioSite,
  type SiteMetadata,
} from '../../redux/api/portfolioApi';
import {
  useLazyListEntitiesQuery,
} from '../../redux/api/entityApi';
import {
  aggregatePortfolioComponents,
  parseSiteLocation,
  getCategoryInfo,
  entityHasModules,
  getModuleCountFromEntity,
  type PortfolioComponentData,
  type PortfolioComponentSummary,
  type SelectedSiteData,
} from '../../services/portfolioComponentService';
import { ComponentColors } from '../graph-viz/types';
import { setCurrentSite } from '../../redux/slices/siteSlice';
import {
  toggleSiteSelection,
  setSelectedSiteIds,
  clearSelectedSites,
  setIsLoading,
  setLoadingProgress,
  setSitesData,
  setPortfolioData,
  type SiteDataWithMetadata,
} from '../../redux/slices/portfolioSiteSelectionSlice';

// Shared model color palette and filter types
import { getModelColor, type ComponentFilter, type ModelWithColor } from '../portfolio/portfolioMapUtils';
import ComponentFilterPanel from '../portfolio/ComponentFilterPanel';

// Fix Leaflet default marker icon issue
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// @ts-ignore
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// ============================================================================
// Types
// ============================================================================

interface SiteWithLocation extends PortfolioSite {
  coordinates: { lat: number; lng: number };
  siteAddress?: string | null;
}

/**
 * Parse coordinates from site_lat_lon which can be:
 * - Array format: [lat, lng] (e.g., [40.5956, -74.2253])
 * - String format: "lat, lng" or "lat,lng" (e.g., "40.5956, -74.2253")
 */
function parseLatLon(latLon: unknown): { lat: number; lng: number } | null {
  if (!latLon) return null;

  // Handle array format: [lat, lng]
  if (Array.isArray(latLon) && latLon.length >= 2) {
    const lat = typeof latLon[0] === 'number' ? latLon[0] : parseFloat(latLon[0]);
    const lng = typeof latLon[1] === 'number' ? latLon[1] : parseFloat(latLon[1]);
    if (!isNaN(lat) && !isNaN(lng)) {
      return { lat, lng };
    }
  }

  // Handle string format: "lat, lng" or "lat,lng"
  if (typeof latLon === 'string') {
    const parts = latLon.split(',').map(p => parseFloat(p.trim()));
    if (parts.length >= 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
      return { lat: parts[0], lng: parts[1] };
    }
  }

  return null;
}

// In-memory cache for geocoded addresses (persists across re-renders)
const geocodeCache = new Map<string, { lat: number; lng: number } | null>();

/**
 * Geocode an address using OpenStreetMap Nominatim
 * Rate limited to 1 request per second per Nominatim usage policy
 */
async function geocodeAddress(address: string): Promise<{ lat: number; lng: number } | null> {
  if (!address || address.trim().length < 5) return null;

  // Check cache first
  const cacheKey = address.toLowerCase().trim();
  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey) || null;
  }

  try {
    const encoded = encodeURIComponent(address);
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&q=${encoded}&limit=1`,
      {
        headers: {
          'User-Agent': 'EngramIQ-WebApp/1.0', // Required by Nominatim
        },
      }
    );

    if (!response.ok) {
      console.warn(`Geocoding failed for "${address}": ${response.status}`);
      geocodeCache.set(cacheKey, null);
      return null;
    }

    const data = await response.json();
    if (data && data.length > 0 && data[0].lat && data[0].lon) {
      const coords = {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
      };
      geocodeCache.set(cacheKey, coords);
      return coords;
    }

    geocodeCache.set(cacheKey, null);
    return null;
  } catch (error) {
    console.warn(`Geocoding error for "${address}":`, error);
    geocodeCache.set(cacheKey, null);
    return null;
  }
}

/**
 * Get coordinates for a site, with priority:
 * 1. metadata.site_lat_lon (if it's in lat,lon format)
 * 2. geocodedCoordinates (from geocoding the address)
 * 3. parseSiteLocation fallback
 */
function getSiteCoordinates(
  site: PortfolioSite,
  metadata?: SiteMetadata | null,
  geocodedCoordinates?: { lat: number; lng: number } | null
): { lat: number; lng: number } | null {
  // First priority: site_lat_lon from metadata (if it's coordinates, not an address)
  if (metadata?.site_lat_lon) {
    const coords = parseLatLon(metadata.site_lat_lon);
    if (coords) return coords;
  }

  // Second priority: geocoded coordinates from address
  if (geocodedCoordinates) {
    return geocodedCoordinates;
  }

  // Fallback to parseSiteLocation (checks site.location and site.address)
  return parseSiteLocation(site);
}

// ============================================================================
// Map Controller Component
// ============================================================================

interface MapControllerProps {
  sites: SiteWithLocation[];
  selectedSiteId: string | null;
  allSites: SiteWithLocation[]; // All sites before filtering - for initial bounds
}

const MapController: React.FC<MapControllerProps> = ({ sites, selectedSiteId, allSites }) => {
  const map = useMap();
  const initialBoundsSetRef = useRef(false);

  // Set initial bounds only once when sites first load
  useEffect(() => {
    if (!initialBoundsSetRef.current && allSites.length > 0) {
      const bounds = L.latLngBounds(allSites.map(s => [s.coordinates.lat, s.coordinates.lng]));
      map.fitBounds(bounds, { padding: [50, 50] });
      initialBoundsSetRef.current = true;
    }
  }, [map, allSites]);

  // Only fly to site when explicitly clicking on a marker (selectedSiteId)
  // Don't zoom/pan on filter changes
  useEffect(() => {
    if (selectedSiteId) {
      const site = sites.find(s => (s.site_id || s.id) === selectedSiteId);
      if (site) {
        map.flyTo([site.coordinates.lat, site.coordinates.lng], 12, { duration: 1 });
      }
    }
  }, [map, sites, selectedSiteId]);

  return null;
};

// ============================================================================
// Site Selection Panel
// ============================================================================

interface SiteSelectionPanelProps {
  sites: PortfolioSite[];
  selectedSiteIds: Set<string>;
  onToggleSite: (siteId: string) => void;
  onSelectAll: () => void;
  onClearAll: () => void;
  onLoadData: () => void;
  isLoading: boolean;
}

const SiteSelectionPanel: React.FC<SiteSelectionPanelProps> = ({
  sites,
  selectedSiteIds,
  onToggleSite,
  onSelectAll,
  onClearAll,
  onLoadData,
  isLoading,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  // Filter by search term (sites already filtered by address in parent)
  const filteredSites = useMemo(() => {
    if (!searchTerm) return sites;
    const term = searchTerm.toLowerCase();
    return sites.filter(site =>
      (site.site_name || site.name || '').toLowerCase().includes(term)
    );
  }, [sites, searchTerm]);

  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="absolute top-4 left-4 z-[1000] rounded-lg p-2 hover:bg-[var(--color-surface-hover)] transition-colors"
        style={{ background: 'var(--color-surface-elevated)', boxShadow: 'var(--shadow-card)' }}
      >
        <Building2 className="w-5 h-5 text-engramiq-green" />
      </button>
    );
  }

  return (
    <div className="absolute top-4 left-4 z-[1000] w-80 backdrop-blur rounded-xl overflow-hidden" style={{ background: 'var(--color-surface-elevated)', boxShadow: 'var(--shadow-card)' }}>
      <div className="flex items-center justify-between p-3" style={{ borderBottom: '1px solid var(--color-overlay-light)' }}>
        <div className="flex items-center gap-2">
          <Building2 className="w-4 h-4 text-engramiq-green" />
          <span className="text-sm font-medium eg-text-primary">Sites</span>
          <span className="text-xs eg-text-secondary">
            ({selectedSiteIds.size} selected)
          </span>
        </div>
        <button
          onClick={() => setIsCollapsed(true)}
          className="p-1 hover:bg-[var(--color-surface-hover)] rounded transition-colors"
        >
          <X className="w-4 h-4 eg-text-muted" />
        </button>
      </div>

      <div className="p-2" style={{ borderBottom: '1px solid var(--color-overlay-light)' }}>
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 eg-text-muted" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search sites..."
            className="w-full pl-8 pr-3 py-1.5 rounded-lg text-xs eg-text-primary focus:outline-none focus:border-engramiq-green"
            style={{ background: 'var(--color-input-bg)', border: '1px solid var(--color-border)' }}
          />
        </div>
        <div className="flex gap-2 mt-2">
          <button
            onClick={onSelectAll}
            className="flex-1 px-2 py-1 text-xs bg-engramiq-green/20 text-engramiq-green rounded hover:bg-engramiq-green/30 transition-colors"
          >
            All
          </button>
          <button
            onClick={onClearAll}
            className="flex-1 px-2 py-1 text-xs eg-text-secondary rounded hover:bg-[var(--color-surface-hover)] transition-colors"
            style={{ background: 'var(--color-surface)' }}
          >
            Clear
          </button>
        </div>
      </div>

      <div className="max-h-60 overflow-auto p-1">
        {filteredSites.map(site => {
          const siteId = site.site_id || site.id || '';
          const isSelected = selectedSiteIds.has(siteId);
          return (
            <button
              key={siteId}
              onClick={() => onToggleSite(siteId)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded text-left transition-colors ${
                isSelected
                  ? 'bg-engramiq-green/20'
                  : 'hover:bg-[var(--color-surface-hover)]'
              }`}
              style={isSelected ? { border: '1px solid rgba(23, 196, 128, 0.3)' } : { border: '1px solid transparent' }}
            >
              {isSelected ? (
                <CheckSquare className="w-3.5 h-3.5 text-engramiq-green flex-shrink-0" />
              ) : (
                <Square className="w-3.5 h-3.5 eg-text-muted flex-shrink-0" />
              )}
              <span className={`text-xs flex-1 truncate ${isSelected ? 'eg-text-primary' : 'eg-text-secondary'}`}>
                {site.site_name || site.name || 'Unknown Site'}
              </span>
            </button>
          );
        })}
      </div>

      <div className="p-2" style={{ borderTop: '1px solid var(--color-overlay-light)' }}>
        <button
          onClick={onLoadData}
          disabled={selectedSiteIds.size === 0 || isLoading}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-engramiq-green text-engramiq-dark-blue text-sm font-medium rounded-lg hover:bg-engramiq-green/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Loading...
            </>
          ) : (
            <>
              <MapPin className="w-4 h-4" />
              Show on Map
            </>
          )}
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Component Filter Panel
// ============================================================================

// ============================================================================
// Main Component
// ============================================================================

const PortfolioMapTab: React.FC = () => {
  const dispatch = useAppDispatch();

  // Redux state
  const selectedPortfolioId = useAppSelector(state => state.portfolio.selectedPortfolioId);
  const organizationState = useAppSelector(s => s.organization);
  const profile = useAppSelector(state => state.auth.profile);

  // Shared portfolio site selection state from Redux
  const {
    selectedSiteIds: selectedSiteIdsArray,
    sitesData,
    portfolioData,
    isLoading,
  } = useAppSelector(state => state.portfolioSiteSelection);

  // Convert array to Set for easier lookups
  const selectedSiteIds = useMemo(() => new Set(selectedSiteIdsArray), [selectedSiteIdsArray]);

  const userEmail = (profile.email || '').toLowerCase();
  const isSuperAdmin = userEmail.endsWith('@engramiq.com');
  const currentOrgId = isSuperAdmin ? organizationState.currentOrganization?.id ?? null : null;

  // Component state (local UI state only)
  const [filter, setFilter] = useState<ComponentFilter>({ category: null, manufacturer: null, model: null });
  const [hoveredSiteId, setHoveredSiteId] = useState<string | null>(null);
  const [selectedMapSiteId, setSelectedMapSiteId] = useState<string | null>(null);
  // Pre-fetched metadata for filtering site list
  const [siteMetadataMap, setSiteMetadataMap] = useState<Map<string, SiteMetadata>>(new Map());
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  // Models with colors from filter panel
  const [filteredModels, setFilteredModels] = useState<ModelWithColor[]>([]);

  // Queries
  const adminArgs = isSuperAdmin
    ? (currentOrgId ? { organization_id: currentOrgId } : undefined)
    : undefined;

  const usePortfolioScoping = !!selectedPortfolioId;

  const { data: adminSitesResponse, isLoading: adminSitesLoading } = useListAdminSitesQuery(
    adminArgs,
    { skip: !isSuperAdmin || usePortfolioScoping }
  );

  const { data: portfolioSitesResponse, isLoading: portfolioSitesLoading } = useListPortfolioSitesQuery(
    selectedPortfolioId ? { portfolio_id: selectedPortfolioId } : { portfolio_id: '' },
    { skip: !selectedPortfolioId }
  );

  // Entity and metadata fetching
  const [fetchEntities] = useLazyListEntitiesQuery();
  const [fetchSiteMetadata] = useLazyGetSiteMetadataQuery();

  // Get available sites
  const availableSites = useMemo(() => {
    if (usePortfolioScoping) {
      return ((portfolioSitesResponse as any)?.items || []) as PortfolioSite[];
    }
    if (isSuperAdmin) {
      return ((adminSitesResponse as any)?.items || []) as PortfolioSite[];
    }
    return ((portfolioSitesResponse as any)?.items || []) as PortfolioSite[];
  }, [usePortfolioScoping, isSuperAdmin, adminSitesResponse, portfolioSitesResponse]);

  const sitesLoading = isSuperAdmin ? adminSitesLoading : portfolioSitesLoading;

  // Pre-fetch metadata for all sites to filter by address
  useEffect(() => {
    const fetchAllMetadata = async () => {
      if (availableSites.length === 0) return;

      setIsLoadingMetadata(true);
      const metadataMap = new Map<string, SiteMetadata>();

      // Fetch metadata in batches to avoid overwhelming the API
      const batchSize = 10;
      for (let i = 0; i < availableSites.length; i += batchSize) {
        const batch = availableSites.slice(i, i + batchSize);
        const promises = batch.map(async (site) => {
          const siteId = site.site_id || site.id || '';
          const portfolioId = site.portfolio_id;
          try {
            const metadata = await fetchSiteMetadata({
              portfolio_id: portfolioId,
              site_id: siteId,
            }).unwrap();
            if (metadata) {
              metadataMap.set(siteId, metadata);
            }
          } catch {
            // Ignore errors for individual sites
          }
        });
        await Promise.all(promises);
      }

      console.log(`[PortfolioMap] Pre-fetched metadata for ${metadataMap.size} of ${availableSites.length} sites`);
      setSiteMetadataMap(metadataMap);
      setIsLoadingMetadata(false);
    };

    fetchAllMetadata();
  }, [availableSites, fetchSiteMetadata]);

  // Filter available sites to only those with site_address in metadata
  const sitesWithAddress = useMemo(() => {
    return availableSites.filter(site => {
      const siteId = site.site_id || site.id || '';
      const metadata = siteMetadataMap.get(siteId);
      return metadata?.site_address != null;
    });
  }, [availableSites, siteMetadataMap]);

  // Sites with valid coordinates (using metadata.site_lat_lon or geocoded address)
  const sitesWithLocation = useMemo((): SiteWithLocation[] => {
    const result = sitesData
      .filter(sd => {
        const coords = getSiteCoordinates(sd.site, sd.metadata, sd.geocodedCoordinates);
        if (!coords) {
          console.warn(`[PortfolioMap] Filtering out site ${sd.site.site_name || sd.site.id} - no coordinates`);
        }
        return coords !== null;
      })
      .map(sd => ({
        ...sd.site,
        coordinates: getSiteCoordinates(sd.site, sd.metadata, sd.geocodedCoordinates)!,
        siteAddress: sd.metadata?.site_address || sd.site.address || sd.site.location || null,
      }));
    console.log(`[PortfolioMap] sitesWithLocation: ${result.length} of ${sitesData.length} sites have coordinates`);
    return result;
  }, [sitesData]);

  // Filter sites based on component filter
  // When a model is selected, we still show all sites with the category
  // but highlight/dim based on whether they have the selected model
  const filteredSites = useMemo((): SiteWithLocation[] => {
    if (!filter.category && !filter.manufacturer && !filter.model) {
      return sitesWithLocation;
    }

    return sitesWithLocation.filter(site => {
      const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === (site.site_id || site.id));
      if (!siteData) return false;

      // When filtering by model, still show all sites with the category
      // The visual highlighting will differentiate which have the model
      return siteData.entities.some(entity => {
        const componentType = entity.concept_id || entity.entity_type_id || entity.pvcollada_component_type || '';
        const { category } = getCategoryInfo(componentType);

        // Special handling for modules: they can be embedded in string entities
        if (filter.category === 'module') {
          return entityHasModules(entity);
        }

        // Only filter by category - model filtering is done visually
        if (filter.category && category !== filter.category) return false;

        if (filter.manufacturer) {
          const manufacturer = entity.manufacturer || entity.properties?.manufacturer || '';
          if (!manufacturer.toLowerCase().includes(filter.manufacturer.toLowerCase())) return false;
        }

        // Don't filter by model here - we want to show all sites with the category
        // and use visual highlighting for model selection
        return true;
      });
    });
  }, [sitesWithLocation, sitesData, filter]);

  // Get component count for a site based on filter
  // When a model is selected, show the category count (not model count) so all sites show meaningful numbers
  const getFilteredComponentCount = useCallback((siteId: string): number => {
    const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
    if (!siteData) return 0;

    if (!filter.category && !filter.manufacturer && !filter.model) {
      return siteData.entities.length;
    }

    // Special handling for modules: count modules from string entities
    if (filter.category === 'module') {
      let moduleCount = 0;
      for (const entity of siteData.entities) {
        moduleCount += getModuleCountFromEntity(entity);
      }
      return moduleCount;
    }

    // Count entities matching the category (not the model) for consistent display
    return siteData.entities.filter(entity => {
      const componentType = entity.concept_id || entity.entity_type_id || entity.pvcollada_component_type || '';
      const { category } = getCategoryInfo(componentType);

      if (filter.category && category !== filter.category) return false;

      if (filter.manufacturer) {
        const manufacturer = entity.manufacturer || entity.properties?.manufacturer || '';
        if (!manufacturer.toLowerCase().includes(filter.manufacturer.toLowerCase())) return false;
      }

      // Don't filter by model for component count - we want to show category count
      // so all sites have meaningful size on the map
      return true;
    }).length;
  }, [sitesData, filter]);

  // Site selection handlers - dispatch to Redux
  const handleToggleSite = useCallback((siteId: string) => {
    dispatch(toggleSiteSelection(siteId));
  }, [dispatch]);

  const handleSelectAll = useCallback(() => {
    // Select all sites that have an address in metadata
    dispatch(setSelectedSiteIds(sitesWithAddress.map(s => s.site_id || s.id || '')));
  }, [dispatch, sitesWithAddress]);

  const handleClearAll = useCallback(() => {
    dispatch(clearSelectedSites());
  }, [dispatch]);

  // Load component data
  const loadComponentData = useCallback(async () => {
    if (selectedSiteIds.size === 0) return;

    dispatch(setIsLoading(true));
    console.log('[PortfolioMap] Loading data for sites:', Array.from(selectedSiteIds));

    const loadedSites: SiteDataWithMetadata[] = [];
    const siteIdsArray = Array.from(selectedSiteIds);
    let geocodeCount = 0;

    for (const siteId of siteIdsArray) {
      const site = availableSites.find(s => (s.site_id || s.id) === siteId);
      if (!site) {
        console.warn(`[PortfolioMap] Site not found: ${siteId}`);
        continue;
      }

      const portfolioId = site.portfolio_id;

      try {
        // Fetch entities and metadata in parallel
        const [entities, metadataResult] = await Promise.all([
          fetchEntities({
            portfolio_id: portfolioId,
            site_id: siteId,
          }).unwrap(),
          fetchSiteMetadata({
            portfolio_id: portfolioId,
            site_id: siteId,
          }).unwrap().catch(() => null), // Don't fail if metadata is unavailable
        ]);

        // Log metadata for debugging
        console.log(`[PortfolioMap] Site ${site.site_name || siteId} metadata:`, {
          site_lat_lon: metadataResult?.site_lat_lon,
          site_address: metadataResult?.site_address,
          site_location: site.location,
          site_address_fallback: site.address,
        });

        // Check if we need to geocode the address
        let geocodedCoordinates: { lat: number; lng: number } | null = null;

        // Only geocode if site_lat_lon is not a valid coordinate string
        const hasValidLatLon = metadataResult?.site_lat_lon &&
          parseLatLon(metadataResult.site_lat_lon) !== null;

        console.log(`[PortfolioMap] Site ${site.site_name || siteId}: hasValidLatLon=${hasValidLatLon}`);

        if (!hasValidLatLon && metadataResult?.site_address) {
          console.log(`[PortfolioMap] Geocoding address: "${metadataResult.site_address}"`);
          // Rate limit: Nominatim requires max 1 request per second
          if (geocodeCount > 0) {
            await new Promise(resolve => setTimeout(resolve, 1100));
          }
          geocodedCoordinates = await geocodeAddress(metadataResult.site_address);
          console.log(`[PortfolioMap] Geocode result:`, geocodedCoordinates);
          geocodeCount++;
        }

        // Final coordinate check
        const finalCoords = getSiteCoordinates(site, metadataResult, geocodedCoordinates);
        console.log(`[PortfolioMap] Site ${site.site_name || siteId} final coordinates:`, finalCoords);

        loadedSites.push({
          site,
          entities: entities || [],
          connections: [],
          metadata: metadataResult,
          geocodedCoordinates,
        });
      } catch (error) {
        console.warn(`Failed to fetch entities for site ${siteId}:`, error);
      }
    }

    console.log(`[PortfolioMap] Loaded ${loadedSites.length} sites`);
    dispatch(setSitesData(loadedSites));
    dispatch(setIsLoading(false));
  }, [selectedSiteIds, availableSites, fetchEntities, fetchSiteMetadata, dispatch]);

  // Aggregate data when sites are loaded
  useEffect(() => {
    if (sitesData.length > 0) {
      const data = aggregatePortfolioComponents(sitesData);
      dispatch(setPortfolioData(data));
    }
  }, [sitesData, dispatch]);

  // Handle site click on map
  const handleMapSiteClick = useCallback((siteId: string) => {
    const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
    if (siteData) {
      setSelectedMapSiteId(siteId);
    }
  }, [sitesData]);

  // Navigate to site detail
  const handleNavigateToSite = useCallback((siteId: string) => {
    const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
    if (siteData) {
      dispatch(setCurrentSite(siteData.site));
      window.dispatchEvent(new CustomEvent('openTab', { detail: { tabType: 'componentlist' } }));
    }
  }, [sitesData, dispatch]);

  // Get component summary info for a site (used for hover tooltip)
  const getSiteComponentInfo = useCallback((siteId: string): { inverterModels: string[]; moduleModels: string[] } => {
    const siteData = sitesData.find(sd => (sd.site.site_id || sd.site.id) === siteId);
    if (!siteData) return { inverterModels: [], moduleModels: [] };

    const inverterModels = new Set<string>();
    const moduleModels = new Set<string>();

    for (const entity of siteData.entities) {
      const componentType = entity.concept_id || entity.entity_type_id || entity.pvcollada_component_type || '';
      const { category } = getCategoryInfo(componentType);
      const model = entity.model || entity.properties?.model || entity.name || '';

      if (category === 'inverter' && model) {
        inverterModels.add(model);
      } else if (category === 'module' && model) {
        moduleModels.add(model);
      }
    }

    return {
      inverterModels: Array.from(inverterModels),
      moduleModels: Array.from(moduleModels),
    };
  }, [sitesData]);

  // Check if site has the selected model
  const siteHasSelectedModel = useCallback((siteId: string): boolean => {
    if (!filter.model) return true; // No model filter, all sites match

    // Check in filteredModels for this model
    const selectedModel = filteredModels.find(m => m.model === filter.model);
    if (selectedModel) {
      return selectedModel.siteIds.has(siteId);
    }
    return true;
  }, [filter.model, filteredModels]);

  // Get marker color based on filter - use category color for all sites
  // Dimming is handled separately via opacity
  const getMarkerColor = useCallback((siteId: string): string => {
    // If category is selected, use the category color for all sites
    if (filter.category) {
      const summary = portfolioData?.summaries.find(s => s.component_category === filter.category);
      return summary?.color || '#10B981';
    }

    return '#10B981'; // Default green
  }, [filter.category, portfolioData]);

  // Get marker opacity based on filter selection
  const getMarkerOpacity = useCallback((siteId: string, isHovered: boolean, isSelected: boolean): number => {
    if (isHovered || isSelected) return 0.95;

    // If model is selected, dim non-matching sites
    if (filter.model) {
      const selectedModel = filteredModels.find(m => m.model === filter.model);
      if (selectedModel && !selectedModel.siteIds.has(siteId)) {
        return 0.25; // Dim non-matching sites
      }
    }

    return 0.7;
  }, [filter.model, filteredModels]);

  // Loading state
  if (sitesLoading) {
    return (
      <div className="w-full h-full flex items-center justify-center" style={{ background: 'var(--color-zone-recessed)' }}>
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-engramiq-green animate-spin mx-auto mb-4" />
          <p className="eg-text-primary">Loading sites...</p>
        </div>
      </div>
    );
  }

  // Default center (US)
  const defaultCenter: [number, number] = [39.8283, -98.5795];
  const defaultZoom = 4;

  return (
    <div className="w-full h-full relative" style={{ background: 'var(--color-zone-recessed)' }}>
      {/* Map Container */}
      <MapContainer
        center={defaultCenter}
        zoom={defaultZoom}
        className="w-full h-full"
        style={{ background: '#0f172a' }}
      >
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
        />

        {filteredSites.length > 0 && (
          <MapController
            sites={filteredSites}
            selectedSiteId={selectedMapSiteId}
            allSites={sitesWithLocation}
          />
        )}

        {filteredSites.map(site => {
          const siteId = site.site_id || site.id || '';
          const count = getFilteredComponentCount(siteId);
          const isHovered = hoveredSiteId === siteId;
          const isSelected = selectedMapSiteId === siteId;
          const color = getMarkerColor(siteId);

          // Size based on component count (log scale)
          const baseSize = 8;
          const maxSize = 24;
          const size = Math.min(baseSize + Math.log10(count + 1) * 6, maxSize);

          // Get component info for popup
          const componentInfo = getSiteComponentInfo(siteId);
          const opacity = getMarkerOpacity(siteId, isHovered, isSelected);
          const hasSelectedModel = siteHasSelectedModel(siteId);

          return (
            <CircleMarker
              key={siteId}
              center={[site.coordinates.lat, site.coordinates.lng]}
              radius={isHovered || isSelected ? size * 1.3 : size}
              pathOptions={{
                fillColor: color,
                fillOpacity: opacity,
                color: isSelected ? '#fff' : color,
                weight: isSelected ? 3 : hasSelectedModel ? 1 : 0.5,
              }}
              eventHandlers={{
                click: () => handleMapSiteClick(siteId),
                mouseover: () => setHoveredSiteId(siteId),
                mouseout: () => setHoveredSiteId(null),
              }}
            >
              <Popup>
                <div className="min-w-56 max-w-72">
                  <h3 className="font-semibold text-gray-900 mb-1">
                    {site.site_name || site.name || 'Unknown Site'}
                  </h3>
                  {site.siteAddress && (
                    <p className="text-xs text-gray-500 mb-2">
                      {site.siteAddress}
                    </p>
                  )}
                  <p className="text-sm text-gray-600 mb-2">
                    {count.toLocaleString()} {filter.category ? getCategoryInfo(filter.category).displayName.toLowerCase() : 'components'}
                  </p>

                  {/* Inverter Models */}
                  {componentInfo.inverterModels.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                        <Zap className="w-3 h-3 text-amber-500" />
                        Inverters:
                      </p>
                      <div className="ml-4 text-xs text-gray-600">
                        {componentInfo.inverterModels.slice(0, 3).map((model, i) => (
                          <div key={i} className="truncate">{model}</div>
                        ))}
                        {componentInfo.inverterModels.length > 3 && (
                          <div className="text-gray-400">+{componentInfo.inverterModels.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Module Models */}
                  {componentInfo.moduleModels.length > 0 && (
                    <div className="mb-2">
                      <p className="text-xs font-medium text-gray-700 flex items-center gap-1">
                        <Package className="w-3 h-3 text-blue-500" />
                        Modules:
                      </p>
                      <div className="ml-4 text-xs text-gray-600">
                        {componentInfo.moduleModels.slice(0, 3).map((model, i) => (
                          <div key={i} className="truncate">{model}</div>
                        ))}
                        {componentInfo.moduleModels.length > 3 && (
                          <div className="text-gray-400">+{componentInfo.moduleModels.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  )}

                  {site.commissioning_date && (
                    <p className="text-xs text-gray-500 mb-2">
                      COD: {new Date(site.commissioning_date).toLocaleDateString()}
                    </p>
                  )}
                  <button
                    onClick={() => handleNavigateToSite(siteId)}
                    className="w-full flex items-center justify-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                  >
                    <Navigation className="w-3.5 h-3.5" />
                    View Site
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          );
        })}
      </MapContainer>

      {/* Site Selection Panel */}
      <SiteSelectionPanel
        sites={sitesWithAddress}
        selectedSiteIds={selectedSiteIds}
        onToggleSite={handleToggleSite}
        onSelectAll={handleSelectAll}
        onClearAll={handleClearAll}
        onLoadData={loadComponentData}
        isLoading={isLoading || isLoadingMetadata}
      />

      {/* Component Filter Panel */}
      {portfolioData && (
        <ComponentFilterPanel
          portfolioData={portfolioData}
          filter={filter}
          onFilterChange={setFilter}
          onModelsChange={setFilteredModels}
        />
      )}

      {/* Status Bar */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-[1000] backdrop-blur rounded-lg px-4 py-2" style={{ background: 'var(--color-surface-elevated)', boxShadow: 'var(--shadow-card)' }}>
        <div className="flex items-center gap-4 text-xs">
          <span className="eg-text-secondary">
            <span className="text-engramiq-green font-medium">{filteredSites.length}</span> sites shown
          </span>
          {portfolioData && (
            <>
              <span className="eg-text-muted">|</span>
              <span className="eg-text-secondary">
                <span className="text-engramiq-green font-medium">
                  {portfolioData.total_components.toLocaleString()}
                </span> total components
              </span>
            </>
          )}
          {filter.category && (
            <>
              <span className="eg-text-muted">|</span>
              <span className="eg-text-secondary flex items-center gap-1">
                <div
                  className="w-2 h-2 rounded"
                  style={{
                    backgroundColor: filter.model
                      ? filteredModels.find(m => m.model === filter.model)?.color
                      : portfolioData?.summaries.find(s => s.component_category === filter.category)?.color
                  }}
                />
                Showing: {getCategoryInfo(filter.category).displayName}
                {filter.model && ` > ${filter.model}`}
              </span>
            </>
          )}
        </div>
      </div>

      {/* Empty State */}
      {sitesData.length === 0 && !isLoading && (
        <div className="absolute inset-0 z-[999] flex items-center justify-center pointer-events-none">
          <div className="eg-card-elevated backdrop-blur p-8 text-center pointer-events-auto max-w-md">
            <MapPin className="w-12 h-12 eg-text-muted mx-auto mb-4" />
            <h2 className="text-lg font-medium eg-text-primary mb-2">
              No sites loaded
            </h2>
            <p className="text-sm eg-text-secondary mb-4">
              Select sites from the panel on the left to view their locations and component distribution on the map.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioMapTab;
